import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import clsx from 'clsx';
import { isNil } from 'lodash';
import { FieldValues, Path, useFormContext } from 'react-hook-form';
import { AutocompleteBase } from '../autocomplete';
import { FlowIcon } from '../flow-icon/FlowIcon';
import { Text } from '../text';

// TODO(jacob): Remove once react-hook-form setValue types are compatible after upgrade.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ReactHookFormBypassType = any;

const ROOT = makeRootClassName('InlineTagsAutocomplete');
const el = makeElementClassNameFactory(ROOT);

export type InlineTagsAutocompleteOption = {
  id: string | number | boolean;
  label: string;
};

interface InlineTagsAutocompleteProps<T> {
  label: string;
  name: Path<T>;
  save: (p: Path<T>) => void;
  required?: boolean;
  options: InlineTagsAutocompleteOption[];
  expandable?: boolean;
  freeSolo?: boolean;
}

export const InlineTagsAutocomplete = <T extends FieldValues>(
  p: InlineTagsAutocompleteProps<T>
) => {
  const { watch, setValue } = useFormContext<T>();
  const value: (string | number | boolean)[] = watch(p.name) ?? [];

  const handleSelect = (
    selectedOptions: (InlineTagsAutocompleteOption | string)[]
  ) => {
    const values = selectedOptions.map((option) =>
      typeof option === 'string' ? option : option.id
    );
    setValue(p.name, values as ReactHookFormBypassType);
    p.save(p.name);
  };

  const placeholder = `Add ${p.label.toLowerCase()}`;
  const selectedValues = value
    .map(
      (v) =>
        p.options.find((o) => o.id === v) ??
        (p.freeSolo ? { id: v, label: String(v) } : undefined)
    )
    .filter((o): o is InlineTagsAutocompleteOption => !isNil(o));

  const hasSelectedValues = selectedValues.length > 0;

  return (
    <div className={clsx(ROOT)}>
      <div className={el`table`}>
        <div className={el`label`}>
          <Text type="body-sm">{p.label}</Text>
        </div>
        <div className={el`field-and-icon-wrapper`}>
          <AutocompleteBase
            multiple
            onChange={(_, value) => {
              handleSelect(value as (InlineTagsAutocompleteOption | string)[]);
            }}
            freeSolo={p.freeSolo}
            submitOnBlur={p.freeSolo}
            options={p.options}
            inputProps={{
              required: p.required,
            }}
            value={selectedValues}
            selectOnFocus
            size="small"
            compact
            blurOnSelect
            transparentWithoutFocus
            inputWrapperClassName={clsx(
              el`input-wrapper`,
              hasSelectedValues ? 'VALID' : 'INVALID',
              p.required && 'REQUIRED'
            )}
            placeholderClassName={clsx(
              el`placeholder`,
              hasSelectedValues ? 'VALID' : 'INVALID',
              p.required && 'REQUIRED'
            )}
            placeholder={hasSelectedValues ? '' : placeholder}
            expandable={p.expandable}
            className={el`field-wrapper`}
            overflow="resize"
          />
          <FlowIcon fieldPath={p.name} />
        </div>
      </div>
    </div>
  );
};
